import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, firstValueFrom, of } from 'rxjs';

/**
 * Runs the OIDC auth check once at application startup, before the router and
 * therefore before AutoLoginAllRoutesGuard.
 *
 * When the app is opened on a login callback URL (?code=...&state=...) whose
 * state no longer matches the value in session storage (browser restarted while
 * sitting on the login page, restored or duplicated tab, URL re-used from
 * history, ...), checkAuth() throws "could not find matching config for state".
 * Without this handler the error hits the route guard, navigation is cancelled
 * and every reload fails again because the stale parameters remain in the URL.
 *
 * On error the stale OIDC parameters are stripped from the URL so that the
 * guard afterwards simply starts a fresh login.
 */
export function checkAuthOnAppStart(oidcSecurityService: OidcSecurityService) {
    return async () => {
        await waitUntilPrerenderingActivation();
        return firstValueFrom(
            oidcSecurityService.checkAuth().pipe(
                catchError(error => {
                    console.warn('Auth check failed on startup, removing stale OIDC parameters from the URL', error);
                    removeAuthParamsFromUrl();
                    return of(null);
                })
            )
        );
    };
}

/**
 * Chrome speculatively prerenders the app when its URL is typed in the address
 * bar. A prerendered page runs on a throwaway clone of sessionStorage that is
 * discarded when the page is activated, and because the IdP is proxied on the
 * same origin the whole login redirect chain can run inside the hidden
 * prerender. The OIDC state written there is lost on activation, which makes
 * the login callback fail with "could not find matching config for state".
 * Blocking app initialization until activation makes the auth flow run against
 * the tab's real storage.
 */
function waitUntilPrerenderingActivation(): Promise<void> {
    const doc = document as Document & { prerendering?: boolean };
    if (!doc.prerendering) {
        return Promise.resolve();
    }
    return new Promise(resolve => doc.addEventListener('prerenderingchange', () => resolve(), { once: true }));
}

function removeAuthParamsFromUrl(): void {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('state')) {
        return;
    }
    for (const param of ['code', 'state', 'session_state', 'iss', 'error', 'error_description']) {
        url.searchParams.delete(param);
    }
    window.history.replaceState(null, '', url.toString());
}
